/**
 * A minimal synchronous key/value abstraction over browser storage.
 *
 * We depend on this interface rather than `window.localStorage` directly so
 * that the progress repository stays testable and degrades gracefully when
 * storage is unavailable (private mode, SSR, disabled cookies).
 */
export interface KeyValueStore {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

/** In-memory fallback used in tests and when `localStorage` is unavailable. */
export function createMemoryStore(): KeyValueStore {
  const map = new Map<string, string>()
  return {
    get: (key) => (map.has(key) ? (map.get(key) as string) : null),
    set: (key, value) => {
      map.set(key, value)
    },
    remove: (key) => {
      map.delete(key)
    },
  }
}

/**
 * Returns the browser `localStorage` wrapped as a `KeyValueStore`, or an
 * in-memory store if access throws (e.g. Safari private mode).
 */
export function getDefaultStore(): KeyValueStore {
  try {
    const probe = '__daily_learning_probe__'
    window.localStorage.setItem(probe, probe)
    window.localStorage.removeItem(probe)
    return {
      get: (key) => window.localStorage.getItem(key),
      set: (key, value) => window.localStorage.setItem(key, value),
      remove: (key) => window.localStorage.removeItem(key),
    }
  } catch {
    return createMemoryStore()
  }
}
